/**
 * MomApproval
 * Approve or reject MoM with integrated JIRA preview
 */

import React, { useState } from 'react';
import { CheckCircle, XCircle, Send, Download } from 'lucide-react';
import { MeetingMinutes, ProposedEpic } from '../../../types';
import { MomViewer } from '../MomViewer';
import { JiraPreview } from '../JiraPreview';
import { Button } from '../../ui/Button';
import { Textarea } from '../../ui/Textarea';
import { Modal } from '../../ui/Modal';
import styles from './MomApproval.module.css';

export interface MomApprovalProps {
  mom: MeetingMinutes;
  onApprove: () => void;
  onReject: (feedback: string) => void;
  onUpdateEpics: (epics: ProposedEpic[]) => void;
  isProcessing?: boolean;
}

export const MomApproval: React.FC<MomApprovalProps> = ({
  mom,
  onApprove,
  onReject,
  onUpdateEpics,
  isProcessing = false,
}) => {
  const [activeTab, setActiveTab] = useState<'mom' | 'jira'>('mom');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectFeedback, setRejectFeedback] = useState('');
  const [showApproveModal, setShowApproveModal] = useState(false);

  const handleReject = () => {
    if (rejectFeedback.trim()) {
      onReject(rejectFeedback);
      setShowRejectModal(false);
      setRejectFeedback('');
    }
  };

  const handleApprove = () => {
    onApprove();
    setShowApproveModal(false);
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export MoM');
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h2 className={styles.title}>Review & Approve</h2>
          <p className={styles.subtitle}>
            Review the Minutes of Meeting and proposed JIRA items before approving
          </p>
        </div>
        <div className={styles.actions}>
          <Button variant="ghost" onClick={handleExport}>
            <Download size={16} />
            Export
          </Button>
          <Button
            variant="ghost"
            onClick={() => setShowRejectModal(true)}
            disabled={isProcessing}
          >
            <XCircle size={16} />
            Reject
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowApproveModal(true)}
            disabled={isProcessing}
          >
            <CheckCircle size={16} />
            {isProcessing ? 'Processing...' : 'Approve & Push to JIRA'}
          </Button>
        </div>
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'mom' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('mom')}
        >
          Minutes of Meeting
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'jira' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('jira')}
        >
          JIRA Items Preview ({mom.proposedEpics.length} Epics)
        </button>
      </div>

      <div className={styles.content}>
        {activeTab === 'mom' ? (
          <MomViewer mom={mom} />
        ) : (
          <JiraPreview
            epics={mom.proposedEpics}
            onUpdateEpics={onUpdateEpics}
            onPushToJira={() => setShowApproveModal(true)}
            isPushing={isProcessing}
          />
        )}
      </div>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject MoM"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleReject}
              disabled={!rejectFeedback.trim()}
            >
              <Send size={16} />
              Send Feedback
            </Button>
          </>
        }
      >
        <div className={styles.modalContent}>
          <p className={styles.modalText}>
            Please provide feedback on what needs to be improved in the Minutes of Meeting.
            The AI will regenerate the MoM based on your feedback.
          </p>
          <Textarea
            value={rejectFeedback}
            onChange={(e) => setRejectFeedback(e.target.value)}
            placeholder="Enter your feedback here..."
            rows={6}
          />
        </div>
      </Modal>

      {/* Approve Modal */}
      <Modal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        title="Approve & Push to JIRA"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowApproveModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleApprove}>
              <CheckCircle size={16} />
              Confirm Approval
            </Button>
          </>
        }
      >
        <div className={styles.modalContent}>
          <p className={styles.modalText}>
            This will approve the Minutes of Meeting and create{' '}
            <strong>{mom.proposedEpics.length} Epics</strong> with{' '}
            <strong>
              {mom.proposedEpics.reduce((sum, epic) => sum + epic.stories.length, 0)} Stories
            </strong>{' '}
            in JIRA. This action cannot be undone.
          </p>
          <div className={styles.confirmList}>
            <div className={styles.confirmItem}>
              <CheckCircle size={16} className={styles.confirmIcon} />
              <span>Approve Minutes of Meeting</span>
            </div>
            <div className={styles.confirmItem}>
              <CheckCircle size={16} className={styles.confirmIcon} />
              <span>Create Epics and Stories in JIRA</span>
            </div>
            <div className={styles.confirmItem}>
              <CheckCircle size={16} className={styles.confirmIcon} />
              <span>Notify team members</span>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MomApproval;
